if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      /**
       * Bind product submission and guard required shipping acknowledgement before app handlers.
       * Keep acknowledgement selections synchronized across forms for the same product.
       */
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.form.querySelector("[name=id]").disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        // Apps can trigger another form's submit from an Add to Cart click.
        this.addEventListener("click", (evt) => {
          const button = evt.target.closest('button[type="submit"], input[type="submit"]');
          if (!button || button.form !== this.form) return;
          const checkbox = this.form.querySelector('[data-free-shipping-excluded-checkbox]');
          if (!checkbox || checkbox.disabled || checkbox.checked) return;
          evt.preventDefault();
          evt.stopImmediatePropagation();
          checkbox.reportValidity();
        }, true);
        this.form.addEventListener("change", (evt) => {
          if (!evt.target.matches('[data-free-shipping-excluded-checkbox]')) return;
          if (this.closest('combined-result-product')) return;
          const productId = this.form.querySelector('[name="product-id"]')?.value;
          if (!productId) return;
          document.querySelectorAll('[data-free-shipping-excluded-checkbox]').forEach((checkbox) => {
            if (checkbox.closest('combined-result-product')) return;
            if (checkbox.form?.querySelector('[name="product-id"]')?.value === productId) {
              checkbox.checked = evt.target.checked;
            }
          });
        });
        this.cart =
          document.querySelector("cart-notification") || document.querySelector("cart-drawer");
        // console.log("this.cart: ", this.cart);
        this.submitButton = this.querySelector('[type="submit"]');
        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      /**
       * Validate enabled required line-item checkboxes before starting Add to Cart.
       * Disabled controls, including those in disabled fieldsets, do not require acknowledgement.
       * @param {SubmitEvent} evt Product-form submission event.
       */
      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;
        if (window.isProductFormSubmitting) return;

        window.isProductFormSubmitting = true;

        this.handleErrorMessage();

        const requiredLineItemCheckboxes = Array.from(
          this.form.querySelectorAll('input[type="checkbox"][name^="properties["][required]')
        );
        const uncheckedRequiredCheckbox = requiredLineItemCheckboxes.find(
          (checkbox) => !checkbox.matches(":disabled") && !checkbox.checked
        );

        if (uncheckedRequiredCheckbox) {
          if (typeof uncheckedRequiredCheckbox.reportValidity === "function") {
            uncheckedRequiredCheckbox.reportValidity();
          }
          window.isProductFormSubmitting = false;
          return;
        }

        this.onSubmitHelper(evt);
      }

      /** Submit the selected product and optional gift wrap, then refresh cart UI and release the submission lock. */
      onSubmitHelper(evt) {
        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading-overlay__spinner").classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const estimatedShipInput = this.form.querySelector(
          'input[name="properties[Estimated to ship on or by]"]'
        );
        if (estimatedShipInput && estimatedShipInput.value.trim() === "") {
          estimatedShipInput.disabled = true;
        }

        const formData = new FormData(this.form);
        const submittedVariantId = formData.get("id");
        this.closest('combined-result-product')?.appendGiftWrap?.(formData);

        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        console.log("Adding to cart:", [...formData]);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: submittedVariantId,
                errors: response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButton.querySelector("span").classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              // console.log(!this.cart);
              // window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: submittedVariantId,
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }

            document.dispatchEvent(
              new CustomEvent("theme:cart-add", {
                bubbles: true,
                cancelable: false,
                detail: { isEmpty: false },
              })
            );
          })
          .catch((e) => {
            console.error(e);
            if (this.closest('combined-result-product')) {
              this.handleErrorMessage('We could not confirm whether the items were added. Please check your cart before trying again.');
            }
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading-overlay__spinner").classList.add("hidden");
            window.isProductFormSubmitting = false;
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(".product-form__error-message");

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
